// Libs
import React, { useState, useContext } from "react";
import { useObserver, FieldType, EditableInput } from "@shoothill/core";
import { FormLabel, Box, Typography, Checkbox, FormControl } from "@material-ui/core";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

// Views
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";

// Styles
import { Row } from "Globals/Styles/LayoutStyles";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { PersonalDetailsFormView } from "./PersonalDetails";

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}
interface ICheckboxProps {
    label: string;
    checked: boolean;
    viewModel: PropertyDetailsViewModel;
    fieldName: keyof FieldType<PropertyDetailsModel>;
    disabled?: boolean;
}

const CheckboxWithLabel: React.FC<ICheckboxProps> = (props: ICheckboxProps) => {
    return useObserver(() => (
        <>
            <Row style={{ alignItems: "center", flexDirection: "row" }}>
                <Checkbox
                    checked={props.checked}
                    onChange={(e) => {
                        props.viewModel.handleCheckboxChange(props.fieldName, e.target.checked);
                    }}
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={props.disabled}
                />
                <Typography variant="body1" style={{ marginLeft: "30px" }}>
                    {props.label}
                </Typography>
            </Row>
        </>
    ));
};

export const PropertyDetailsView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.propertyDetailsViewModel);

    return useObserver(() => (
        <div onMouseDown={() => viewModel.restartTimer()} onKeyUp={() => viewModel.restartTimer()}>
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            {/* Start - Checkboxes */}
            <CheckboxWithLabel
                disabled={props.applicationFormViewModel.model.isSubmitted}
                label="Council tenant"
                checked={viewModel.model.isCouncilTenant}
                fieldName="isCouncilTenant"
                viewModel={viewModel}
            />
            <CheckboxWithLabel
                disabled={props.applicationFormViewModel.model.isSubmitted}
                label="Housing association tenant"
                checked={viewModel.model.isHousingAssociationTenant}
                fieldName="isHousingAssociationTenant"
                viewModel={viewModel}
            />
            <CheckboxWithLabel
                disabled={props.applicationFormViewModel.model.isSubmitted}
                label="Renting privately"
                checked={viewModel.model.isRentingProperty}
                fieldName="isRentingProperty"
                viewModel={viewModel}
            />
            <CheckboxWithLabel
                disabled={props.applicationFormViewModel.model.isSubmitted}
                label="Renting from employer"
                checked={viewModel.model.isRentingFromEmployer}
                fieldName="isRentingFromEmployer"
                viewModel={viewModel}
            />
            <CheckboxWithLabel
                disabled={props.applicationFormViewModel.model.isSubmitted}
                label="Living with family or friend"
                checked={viewModel.model.isLivingWithFamily}
                fieldName="isLivingWithFamily"
                viewModel={viewModel}
            />
            <CheckboxWithLabel
                disabled={props.applicationFormViewModel.model.isSubmitted}
                label="Temporary accommodation"
                checked={viewModel.model.isInTempAccommodation}
                fieldName="isInTempAccommodation"
                viewModel={viewModel}
            />
            <CheckboxWithLabel
                disabled={props.applicationFormViewModel.model.isSubmitted}
                label="Home owner"
                checked={viewModel.model.isHomeOwner}
                fieldName="isHomeOwner"
                viewModel={viewModel}
            />
            {/* End - Checkboxes */}
            {viewModel.model.isRentingProperty && (
                <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                    <Row>
                        <EditableInput
                            label="Landlord's name"
                            viewModel={viewModel}
                            fieldName="landlordName"
                            placeHolder="Your landlord's name"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                        <EditableInput
                            label="Landlord's telephone number"
                            viewModel={viewModel}
                            fieldName="landlordPhone"
                            placeHolder="Your landlord's telephone number"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </Row>
                    <Row>
                        <EditableInput
                            label="Landlord's address line 1"
                            placeHolder="Address line 1"
                            viewModel={viewModel}
                            fieldName="landlordAddressLine1"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <EditableInput
                            label="Landlord's address line 2"
                            placeHolder="Address line 2"
                            viewModel={viewModel}
                            fieldName="landlordAddressLine2"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <EditableInput
                            label="Landlord's address line 3"
                            placeHolder="Address line 3"
                            viewModel={viewModel}
                            fieldName="landlordAddressLine3"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                    <Row>
                        <EditableInput
                            label="Landlord's city/town"
                            placeHolder="City/Town"
                            viewModel={viewModel}
                            fieldName="landlordCity"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <EditableInput
                            label="Landlord's county"
                            placeHolder="County"
                            viewModel={viewModel}
                            fieldName="landlordCounty"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                        <EditableInput
                            label="Landlord's postcode"
                            placeHolder="Postcode"
                            viewModel={viewModel}
                            fieldName="landlordPostcode"
                            shrink={true}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    </Row>
                </Box>
            )}

            {/* SW removed for 11548 */}
            <Row style={{ display: "none" }}>
                <CustomYesNoRadio
                    label={
                        <>
                            <span>Are you registered with the local Help to Buy agent?</span>
                            <p style={{ fontSize: "13px", marginTop: "4px", marginBottom: "8px" }}>(e.g. Help to Buy South)</p>
                        </>
                    }
                    fieldName={"isRegisteredWithHelpToBuy"}
                    value={viewModel.model.isRegisteredWithHelpToBuy}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("isRegisteredWithHelpToBuy")}
                />
                {viewModel.model.isRegisteredWithHelpToBuy && (
                    <FormControl component="fieldset">
                        <FormLabel style={{ marginBottom: "8px" }} component="legend">
                            Please give registration number
                        </FormLabel>
                        <EditableInput
                            label={
                                <span style={{ fontSize: "13px" }}>
                                    (Don't have one? You can register <a style={{ color: "#007AFF", cursor: "pointer" }}>here</a>)
                                </span>
                            }
                            viewModel={viewModel}
                            fieldName="helpToBuyRegistrationNumber"
                            placeHolder="Your help to buy registration number"
                            shrink={true}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                            onChange={() => {
                                viewModel.handleInputChange();
                            }}
                        />
                    </FormControl>
                )}
            </Row>
            <Row>
                <CustomYesNoRadio
                    label={
                        <>
                            <span>Do you have any pets?</span>
                            <p style={{ fontSize: "13px", marginTop: "4px", marginBottom: "8px" }}>(Please note, we operate a no pets policy in our apartments)</p>
                        </>
                    }
                    fieldName={"hasPets"}
                    value={viewModel.model.hasPets}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("hasPets")}
                />
            </Row>
            {viewModel.model.hasPets && (
                <Row>
                    <EditableInput
                        label="Please state"
                        viewModel={viewModel}
                        fieldName="petsNotes"
                        placeHolder="Your pets"
                        shrink={true}
                        inputProps={{
                            disabled: props.applicationFormViewModel.model.isSubmitted,
                        }}
                        onChange={() => {
                            viewModel.handleInputChange();
                        }}
                    />
                </Row>
            )}
            <Row style={{ marginBottom: "14px" }}>
                <CustomYesNoRadio
                    label={
                        <>
                            <span>Will anyone else be living with you?</span>
                            <p style={{ fontSize: "13px", marginTop: "4px", marginBottom: "8px" }}>
                                (Person(s) not included in application process for the purchase of this property)
                            </p>
                        </>
                    }
                    fieldName={"isAnyoneLivingWithYou"}
                    value={viewModel.model.isAnyoneLivingWithYou}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getValue("isAnyoneLivingWithYou") ? viewModel.getError("isAnyoneLivingWithYou") : ""}
                />
            </Row>
            {viewModel.model.isAnyoneLivingWithYou && <PersonalDetailsFormView propertyDetailsViewModel={viewModel} applicationFormViewModel={props.applicationFormViewModel} />}
        </div>
    ));
};
